import { useEffect, useState } from 'react'
import { Mail } from 'lucide-react'
import Button from '../../components/Button'
import CkEditorField from '../../components/CkEditorField'
import DateTimePicker from '../../components/DateTimePicker'
import { FormBody, FormCols } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Modal from '../../components/Modal'
import TextField from '../../components/TextField'
import { strings as S } from './strings'

const EMPTY = { title: '', sendTime: '', body: '' }

/**
 * `#mdlSendEmail` — schedules one message to one person.
 *
 * THE BUTTON USED TO BE A TOAST. `UserDetailsPage` answered its own Send Email action with
 * `toast.success('تم جدولة البريد بنجاح')` — a hardcoded Arabic string, shown to English
 * readers too, for a message that was never composed and never sent. `sendEmail()` already
 * existed in the service and was already wired to `POST /UserDetailsAdmin/SendEmail`; it
 * had no caller.
 *
 * THE BODY IS A RICH EDITOR, as `Views/UserDetailsAdmin/NotifyUser.cshtml` gives it — the
 * same CKEditor build the awareness bulletin uses. The legacy then throws most of that
 * away: its submit harvests the body with `$("div.ck-editor__main p").text()`, so what
 * reaches the server is the plain text of the paragraphs and every heading, list and link
 * the editor just offered is lost. This sends the editor's HTML instead, which is what the
 * notification payload carries and what an email can render.
 *
 * Validation is the legacy's, in its order and its wording: title, then send date, then
 * body, one message at a time (`NotifyUser.cshtml:58-106`).
 */
export default function UserEmailModal({ open, user, saving, onClose, onSubmit }) {
  const [values, setValues] = useState(EMPTY)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setValues(EMPTY)
    setError('')
  }, [open, user])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  /** CKEditor gives `<p>&nbsp;</p>` for an empty document, which is not a message. */
  const bodyIsEmpty = (html) =>
    !String(html ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim()

  const submit = (event) => {
    event.preventDefault()
    /* The modal sits inside the details page; without this the page's own form — and the
       browser's default navigation — take the submit as well. */
    event.stopPropagation()

    if (!values.title.trim()) return setError(S.emailModal.missingTitle)
    if (!values.sendTime.trim()) return setError(S.emailModal.missingSendTime)
    if (bodyIsEmpty(values.body)) return setError(S.emailModal.missingBody)

    setError('')
    return onSubmit(values)
  }

  return (
    <Modal
      open={open}
      size="lg"
      title={S.emailModal.title}
      icon={Mail}
      subtitle={user?.name}
      onClose={onClose}
      footer={
        <Button type="submit" form="user-email-form" disabled={saving}>
          {S.emailModal.submit}
        </Button>
      }
    >
      <form id="user-email-form" onSubmit={submit} noValidate>
        <FormBody>
          <FormCols>
            <FormRow label={S.emailModal.messageTitle}>
              {(id) => (
                <TextField
                  id={id}
                  value={values.title}
                  onChange={set('title')}
                  autoComplete="off"
                />
              )}
            </FormRow>

            {/* DATE AND TIME. A send time that is only a date schedules everything for
                midnight, which is not when anyone wants a message to arrive — and the
                legacy's own field is a flatpickr with `enableTime` and `Y-m-d H:i`. */}
            <FormRow label={S.emailModal.sendDate}>
              {(id) => (
                <DateTimePicker id={id} value={values.sendTime} onChange={set('sendTime')} />
              )}
            </FormRow>
          </FormCols>

          <FormRow label={S.emailModal.messageBody}>
            {(id) => (
              <CkEditorField
                id={id}
                value={values.body}
                onChange={set('body')}
                minHeight={260}
              />
            )}
          </FormRow>

          {error && (
            <p className="form-row__error" role="alert">
              <bdi>{error}</bdi>
            </p>
          )}
        </FormBody>
      </form>
    </Modal>
  )
}
